/**
 * How and when outcomes settle (#777): a live example from outcomeMeta (described by describe/, the same code as the
 * market page) and an interactive settlement timeline. Facts and sources: docs/research/EDUCATION_SOURCES.md.
 */
import { useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useEnv } from '../config/env';
import { formatUtc } from '../describe';
import { CategoryChip, Countdown, DetailsTable, LocalTime, RawDescription } from '../describe/ui';
import type { Side } from '../hl/outcomes';
import { formatShares, formatUsd } from '../market/format';
import { deployerMultiplier } from '../trading/fees';
import { settlementResult } from './calc';
import type { LearnExample } from './example';
import type { LearnExamples } from './useLearnExamples';
import { whenLabel } from '../describe/when';
import { networkWord } from '../i18n/common';
import { localizeNumber } from '../i18n/format';
import Msg from '../i18n/Msg';
import { useT } from '../i18n/useT';

/** A live example market, selectable among a few with a future resolution time. */
export function LiveExample({ example, examples, onPick, state }: {
  example: LearnExample | undefined;
  examples: LearnExample[];
  onPick: (id: number) => void;
  state: 'loading' | 'error' | 'ready';
}) {
  const selectId = useId();
  const { env } = useEnv();
  const t = useT();
  const network = networkWord(env.name);
  if (state === 'loading') return <div className="card p-4 text-sm text-muted">{t('learn.example.loading', { network })}</div>;
  if (state === 'error' || !example) {
    return (
      <div className="card p-4 text-sm text-muted" data-testid="learn-example-empty">
        {state === 'error'
          ? t('learn.example.error')
          : t('learn.example.none', { network })}
      </div>
    );
  }
  const { meta, desc } = example;
  const mult = deployerMultiplier(meta.deployerFeeScale);
  return (
    <div className="card space-y-3 p-4" data-testid="learn-example">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted">{t('learn.example.eyebrow', { network })}</p>
        {examples.length > 1 && (
          <label htmlFor={selectId} className="flex items-center gap-2 text-xs text-muted">
            {t('learn.example.pick')}
            <select
              id={selectId}
              value={meta.outcome}
              onChange={(e) => onPick(Number(e.target.value))}
              className="max-w-[14rem] truncate rounded-md border border-border bg-bg px-2 py-1 text-xs text-fg"
              data-testid="learn-example-select"
              translate="no"
            >
              {examples.map((e) => (
                <option key={e.meta.outcome} value={e.meta.outcome}>
                  {e.desc.title}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
          <CategoryChip category={desc.category} />
          <span className="num">#{meta.outcome}</span>
          <span data-testid="learn-example-settler">
            {example.settledBy === 'protocol' ? t('learn.example.by_protocol') : t('learn.example.by_venue', { venue: String(meta.venue) })}
          </span>
        </div>
        <h3 className="break-words text-lg font-semibold leading-snug" data-testid="learn-example-title" translate="no">
          {desc.title}
        </h3>
        {desc.resolvesAt && (
          <p className="text-sm" data-testid="learn-example-time">
            {whenLabel(desc.resolvesLabel)} <LocalTime date={desc.resolvesAt} />{' '}
            <span className="num text-xs text-muted">({formatUtc(desc.resolvesAt)})</span> ·{' '}
            <Countdown date={desc.resolvesAt} label={desc.resolvesLabel} />
          </p>
        )}
      </div>
      <DetailsTable rows={desc.details} />
      <p className="text-xs text-muted">
        <Msg
          k="learn.example.mult"
          args={{
            mult: <span className="num">×{localizeNumber(mult.factor)}</span>,
            scale: meta.deployerFeeScale
              ? t('learn.example.scale', { scale: localizeNumber(meta.deployerFeeScale) })
              : t('learn.example.no_scale'),
            first: (
              <span className="text-yes" translate="no">
                {desc.sideLabels[0]}
              </span>
            ),
            second: (
              <span className="text-no" translate="no">
                {desc.sideLabels[1]}
              </span>
            ),
          }}
        />
      </p>
      <RawDescription name={meta.name} description={meta.description} label={t('learn.example.raw')} />
      <Link to={`/market/${meta.outcome}`} className="inline-block text-sm text-accent hover:underline">
        {t('learn.example.open')}
      </Link>
    </div>
  );
}

type Scenario = 'yes' | 'no' | 'fraction';
const SCENARIO_FRACTION: Record<Scenario, string> = { yes: '1', no: '0', fraction: '0.25' };

interface Step {
  key: string;
  label: string;
  when: string;
  body: ReactNode;
}

/** Interactive settlement timeline: pick a step to read what happens, pick a result to see the payout. */
export function SettlementTimeline({ example, side, shares, cost }: {
  example: LearnExample | undefined;
  side: Side;
  shares: string;
  cost: string | null;
}) {
  const [step, setStep] = useState(0);
  const [scenario, setScenario] = useState<Scenario>('yes');
  const t = useT();
  const sideName = t(side === 0 ? 'common.yes' : 'common.no');
  const resolves = example?.desc.resolvesAt;
  const protocol = example ? example.settledBy === 'protocol' : false;
  const result = settlementResult(side, shares, SCENARIO_FRACTION[scenario], cost, example?.meta.deployerFeeScale);

  const steps: Step[] = [
    {
      key: 'trade',
      label: t('learn.step.trade.label'),
      when: t('learn.step.trade.when'),
      body: <p>{t('learn.step.trade.p1')}</p>,
    },
    {
      key: 'resolve',
      label: t('learn.step.resolve.label'),
      when: resolves
        ? t('learn.step.resolve.when_at', { label: whenLabel(example?.desc.resolvesLabel), time: formatUtc(resolves) })
        : t('learn.step.resolve.when_rules'),
      body: (
        <>
          <p>{t('learn.step.resolve.p1')}</p>
          <p className="text-muted">{t('learn.step.resolve.p2')}</p>
        </>
      ),
    },
    {
      key: 'settle',
      label: t('learn.step.settle.label'),
      when: protocol ? t('learn.step.settle.when_auto') : t('learn.step.settle.when_soon'),
      body: (
        <>
          <p>{t('learn.step.settle.p1')}</p>
          <p data-testid="settle-timing">{t('learn.step.settle.timing')}</p>
          <p>
            <Msg k="learn.step.settle.p2" args={{ canceled: <strong>{t('learn.step.settle.canceled')}</strong> }} />
          </p>
        </>
      ),
    },
    {
      key: 'payout',
      label: t('learn.step.payout.label'),
      when: t('learn.step.payout.when'),
      body: (
        <>
          <p>{t('learn.step.payout.p1')}</p>
          <p className="text-muted">{t('learn.step.payout.p2')}</p>
        </>
      ),
    },
  ];
  const cur = steps[step]!;

  return (
    <div className="space-y-3" data-testid="settle-timeline">
      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label={t('learn.timeline.aria')}>
        {steps.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => setStep(i)}
              aria-current={step === i ? 'step' : undefined}
              aria-controls="settle-step-panel"
              className={`w-full rounded-md border px-3 py-2 text-start ${
                step === i ? 'border-accent bg-accent/10' : 'border-border hover:border-fg/40'
              }`}
              data-testid={`settle-step-${s.key}`}
            >
              <span className="block text-[11px] text-muted">{t('learn.timeline.step', { n: i + 1 })}</span>
              <span className="block text-sm font-semibold">{s.label}</span>
              <span className="block break-words text-[11px] text-muted">{s.when}</span>
            </button>
          </li>
        ))}
      </ol>
      <div id="settle-step-panel" className="space-y-2 rounded-md bg-surface-2/50 p-3 text-sm" aria-live="polite" data-testid="settle-step-panel">
        <h4 className="font-semibold">{cur.label}</h4>
        {cur.body}
      </div>

      <div className="rounded-md border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">
            <Msg
              k="learn.timeline.position"
              args={{ shares: <span className="num">{formatShares(shares)}</span>, side: sideName }}
            />
          </span>
          <div role="radiogroup" aria-label={t('learn.timeline.result_group')} className="flex flex-wrap gap-1">
            {(
              [
                ['yes', t('learn.timeline.yes_wins')],
                ['no', t('learn.timeline.no_wins')],
                ['fraction', t('learn.timeline.fraction')],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={scenario === k}
                onClick={() => setScenario(k)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  scenario === k ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {result ? (
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4" aria-live="polite" data-testid="settle-payout">
            <div>
              <dt className="text-xs text-muted">{t('learn.timeline.value')}</dt>
              <dd className="num" data-testid="settle-value">{formatUsd(result.value)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t('learn.timeline.fee')}</dt>
              <dd className="num">{formatUsd(result.fee, 4)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t('learn.timeline.receive')}</dt>
              <dd className="num">{formatUsd(result.net)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted">{t('learn.timeline.pnl')}</dt>
              <dd
                className={`num ${result.pnl && result.pnl.startsWith('-') ? 'text-no' : 'text-yes'}`}
                data-testid="settle-pnl"
              >
                {result.pnl === null ? '—' : result.pnl.startsWith('-') ? `−${formatUsd(result.pnl.slice(1))}` : `+${formatUsd(result.pnl)}`}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="mt-2 text-xs text-muted">{t('learn.timeline.enter_valid')}</p>
        )}
      </div>
    </div>
  );
}

/** The live example and the timeline share one selected market (picked in LearnPage via useLearnExamples). */
export default function SettlementSection({ ex, side, shares, cost }: {
  ex: LearnExamples;
  side: Side;
  shares: string;
  cost: string | null;
}) {
  return (
    <div className="space-y-4">
      <LiveExample example={ex.example} examples={ex.examples} onPick={ex.pick} state={ex.state} />
      <SettlementTimeline example={ex.example} side={side} shares={shares} cost={cost} />
    </div>
  );
}
